import { isStaffRole } from "@saleor/auth/hooks/useUserRole";
import { showNotifyMessage } from "@saleor/changeOrder/utils";
import AppHeader from "@saleor/components/AppHeader";
import Container from "@saleor/components/Container";
import MyTypography from "@saleor/components/MyTypography";
import PageHeader from "@saleor/components/PageHeader";
import { defaultListSettings } from "@saleor/config";
import DialogCreateOrderError from "@saleor/createOrder/components/DialogCreateOrderError";
import { orderCartPath } from "@saleor/createOrder/urls";
import CartDetailTable from "@saleor/createOrderExport/components/CartDetailTable";
import HeaderInformation from "@saleor/createOrderExport/components/HeaderInformation";
import {
  OrderDirection,
  ScgExportCartItemOrderField,
  ScgpExportErrorCode,
  useCreateExportOrderMutation,
  useGetCartDetailDataQuery,
  useUpdateDraftExportCartMutation
} from "@saleor/graphql";
import useListSettings from "@saleor/hooks/useListSettings";
import useLocalPaginator, {
  useLocalPaginationState
} from "@saleor/hooks/useLocalPaginator";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { makeStyles } from "@saleor/macaw-ui";
import { ListViews } from "@saleor/types";
import { mapEdgesToItems } from "@saleor/utils/maps";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import useRouter from "use-react-router";

const useStyles = makeStyles(
  theme => ({
    inlineBlock: {
      display: "inline-block",
      color: theme.palette.grey[900]
    }
  }),
  { name: "CartView" }
);

interface CartDetailExportProps {
  checkOutId: string;
}

const CartDetailExport: React.FC<CartDetailExportProps> = props => {
  const { checkOutId } = props;
  const intl = useIntl();
  const { history } = useRouter();
  const navigate = useNavigator();
  const notify = useNotifier();
  const classes = useStyles();

  const [openCreateOrderError, setOpenCreateOrderError] = useState<boolean>(
    false
  );

  const [popUpContent, setPopUpContent] = useState(null);

  const toggleCloseCreateOrderError = () => {
    setOpenCreateOrderError(val => !val);
  };

  const handleBack = () => {
    history.goBack();
  };

  const { updateListSettings, settings } = useListSettings(
    ListViews.ORDER_EXPORT_DETAIL_LIST
  );

  const [
    valuesPaginationState,
    setValuesPaginationState
  ] = useLocalPaginationState(settings?.rowNumber);

  const paginateValues = useLocalPaginator(setValuesPaginationState);

  const queryVariables = React.useMemo(
    () => ({
      ...valuesPaginationState,
      sortBy: {
        direction: OrderDirection.ASC,
        field: ScgExportCartItemOrderField.CONTRACT_MATERIAL_ID
      },
      id: checkOutId
    }),
    [checkOutId, settings.rowNumber, valuesPaginationState]
  );

  const { data, loading, refetch } = useGetCartDetailDataQuery({
    variables: queryVariables
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginateValues(
    data?.exportCart?.items?.pageInfo,
    valuesPaginationState
  );

  const resetPaginationState = () => {
    setValuesPaginationState({
      first: settings?.rowNumber,
      after: undefined,
      before: undefined
    });
  };

  const lastPaginationState = () => {
    setValuesPaginationState({
      last: data?.exportCart?.items?.latestPageItemNumber
    });
  };
  const handlePreviousFirstPage = () => {
    resetPaginationState();
  };
  const handleNextLastPage = () => {
    lastPaginationState();
  };

  const isStaff = isStaffRole();

  const [
    createExportOrder,
    { loading: loadingCreateOrder }
  ] = useCreateExportOrderMutation({
    onCompleted: data => {
      if (
        data.createExportOrder.order === null &&
        data.createExportOrder.errors.length === 0 &&
        !isStaff
      ) {
        setPopUpContent(
          <span>
            <MyTypography variant="body2" className={classes.inlineBlock}>
              เนื่องจาก Customer Credit :
            </MyTypography>{" "}
            <MyTypography variant="subtitle2" className={classes.inlineBlock}>
              Credit Blocked
            </MyTypography>
          </span>
        );
        toggleCloseCreateOrderError();
        return;
      }
      if (data?.createExportOrder?.order?.id) {
        navigate(orderCartPath(data.createExportOrder.order.id));
      }
      if (data?.createExportOrder?.errors?.length) {
        if (
          ScgpExportErrorCode.PRODUCT_GROUP_ERROR ===
          data?.createExportOrder?.errors?.[0].code
        ) {
          setPopUpContent(
            <MyTypography variant="body2" className={classes.inlineBlock}>
              กรุณาเลือก{" "}
              <MyTypography variant="subtitle2" className={classes.inlineBlock}>
                สินค้ากลุ่มเดียวกัน
              </MyTypography>{" "}
              ในการสร้างคำสั่งซื้อ
            </MyTypography>
          );
          toggleCloseCreateOrderError();
        } else {
          showNotifyMessage(
            notify,
            data?.createExportOrder?.errors,
            [],
            [],
            []
          );
        }
      }
    }
  });

  const onSubmitCreate = listSelected => {
    createExportOrder({
      variables: {
        input: {
          orderHeader: {
            piId: data?.exportCart?.pi?.id
          },
          lines: listSelected.map(line => ({
            cartItemId: line.id,
            quantity: line.quantity,
            piProduct: line.piProduct.id
          }))
        }
      }
    });
  };

  const [
    updateExportCart,
    { loading: loadingUpdateExportCart }
  ] = useUpdateDraftExportCartMutation();

  const handleUpdateExportCart = itemCart => {
    if (itemCart.errorQuantity) {
      return;
    }
    const dataRoot = mapEdgesToItems(data?.exportCart?.items);
    const itemRoot = dataRoot.find(item => item.id === itemCart.id);
    if (Number(itemRoot.quantity) !== Number(itemCart.quantity)) {
      if (!loadingUpdateExportCart) {
        updateExportCart({
          variables: {
            cartId: checkOutId,
            input: {
              lines: [{ quantity: itemCart.quantity, id: itemCart.id }]
            }
          }
        }).then(response => {
          if (response?.data?.updateDraftExportCart?.cart?.id) {
            refetch();
          }
        });
      }
    }
  };

  useEffect(() => {
    updateListSettings(
      "rowNumber",
      defaultListSettings.ORDER_EXPORT_DETAIL_LIST.rowNumber
    );
  }, []);

  useEffect(() => {
    resetPaginationState();
  }, [settings?.rowNumber]);

  return (
    <>
      <Container>
        <AppHeader onBack={handleBack}>
          <MyTypography variant="body3">
            {intl.formatMessage({ defaultMessage: "กลับไป" })}
          </MyTypography>
        </AppHeader>
        <PageHeader
          title={intl.formatMessage({ defaultMessage: "Shopping Cart" })}
        />
        <HeaderInformation
          dataInfo={{
            piNo: data?.exportCart?.pi?.code || "",
            soldToCode: data?.exportCart?.soldTo?.code || "",
            soldToName: data?.exportCart?.soldTo?.name || ""
          }}
        />
        <CartDetailTable
          onSubmitCart={onSubmitCreate}
          data={mapEdgesToItems(data?.exportCart?.items)}
          dataInfo={{
            checkOutId,
            piId: data?.exportCart?.pi?.id || "",
            piNo: data?.exportCart?.pi?.code || "",
            soldTo: data?.exportCart?.soldTo?.code
              ? `${data?.exportCart?.soldTo?.code} - ${data?.exportCart?.soldTo?.name}`
              : ""
          }}
          currency={data?.exportCart?.pi?.currency}
          refetch={refetch}
          disabled={loading}
          loading={loading || loadingCreateOrder || loadingUpdateExportCart}
          settings={settings}
          onUpdateListSettings={updateListSettings}
          onNextPage={loadNextPage}
          onPreviousPage={loadPreviousPage}
          onNextLastPage={handleNextLastPage}
          onPreviousFirstPage={handlePreviousFirstPage}
          totalCount={data?.exportCart?.items?.totalCount}
          latestPageItemNumber={data?.exportCart?.items?.latestPageItemNumber}
          pageInfo={pageInfo}
          onRowClick={() => null}
          handleUpdateExportCart={handleUpdateExportCart}
        />
        <DialogCreateOrderError
          onConfirm={toggleCloseCreateOrderError}
          open={openCreateOrderError}
          okText="ปิดหน้าต่าง"
          title="ไม่สามารถดำเนินการต่อได้"
          content={popUpContent}
        />
      </Container>
    </>
  );
};

export default CartDetailExport;
